/**
 * ApplicationGroupPage
 * Main page for Application Group dashboard (Grupo de Aplicação)
 * Read-only consolidated view of risks, exams, and source generators
 *
 * @version 2025.1.0
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Button,
  Chip,
  Stack,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  Business as CompanyIcon,
  Warning as RiskIcon,
  Science as ExamIcon,
  Factory as SourceIcon,
  ArrowForward as NavigateIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PageShell, DataTable, type DataTableColumn } from '../../components';
import { companyService } from '../../services/companyService';
import { useApplicationGroups } from './hooks/useApplicationGroups';
import {
  ConsolidatedItem,
  ConsolidatedItemType,
  getConsolidatedItemTypeLabel,
  getConsolidatedItemTypeColor,
  formatDate,
} from '../../types/applicationGroup';

// ============================================================================
// TYPES
// ============================================================================

interface SelectOption {
  id: number;
  name: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function TabPanel({ children, value, index }: TabPanelProps): React.ReactElement | null {
  if (value !== index) return null;
  return <Box sx={{ pt: 3 }}>{children}</Box>;
}

const consolidatedColumns: DataTableColumn<ConsolidatedItem>[] = [
  {
    id: 'name',
    label: 'Nome',
    primary: true,
    render: (item) => (
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          label={getConsolidatedItemTypeLabel(item.type)}
          size="small"
          color={getConsolidatedItemTypeColor(item.type)}
        />
        <Typography variant="body2">{item.name}</Typography>
      </Stack>
    ),
  },
  { id: 'code', label: 'Código', render: (item) => item.code || '-' },
  { id: 'targetName', label: 'Aplicado Em', render: (item) => item.targetName },
  { id: 'appliedAt', label: 'Data', render: (item) => formatDate(item.appliedAt) },
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ApplicationGroupPage: React.FC = () => {
  const navigate = useNavigate();

  // Application groups hook
  const {
    consolidated,
    loading,
    error,
    setSelectedCompanyId,
    refresh,
    clearError,
  } = useApplicationGroups();

  // Local UI state
  const [activeTab, setActiveTab] = useState(0);
  const [localSelectedCompanyId, setLocalSelectedCompanyId] = useState<number | null>(null);
  const [companies, setCompanies] = useState<SelectOption[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Pagination state for each tab
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ============================================================================
  // LOAD COMPANIES
  // ============================================================================

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setLoadingCompanies(true);
        const response = await companyService.getCompanies({ limit: 1000 });
        setCompanies(
          response.data.map((c) => ({
            id: Number(c.id),
            name: c.tradeName || c.legalName,
          }))
        );
      } catch (err) {
        console.error('Error loading companies:', err);
      } finally {
        setLoadingCompanies(false);
      }
    };
    loadCompanies();
  }, []);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleCompanyChange = useCallback(
    (companyId: number) => {
      setLocalSelectedCompanyId(companyId);
      setSelectedCompanyId(companyId);
      setPage(0); // Reset pagination when company changes
    },
    [setSelectedCompanyId]
  );

  const handleNavigateTo = (path: string) => {
    navigate(path);
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setPage(0); // Reset pagination when tab changes
  };

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const renderCompanySelector = () => (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Selecionar Empresa"
        action={
          localSelectedCompanyId && (
            <Button
              startIcon={<RefreshIcon />}
              onClick={refresh}
              disabled={loading}
            >
              Atualizar
            </Button>
          )
        }
      />
      <CardContent>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Empresa</InputLabel>
              <Select
                value={localSelectedCompanyId || ''}
                label="Empresa"
                onChange={(e) => handleCompanyChange(Number(e.target.value))}
                disabled={loadingCompanies}
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {localSelectedCompanyId && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Chip
                icon={<CompanyIcon />}
                label={companies.find((c) => c.id === localSelectedCompanyId)?.name || ''}
                color="primary"
                variant="outlined"
              />
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );

  const renderNavigationButtons = () => (
    <Card sx={{ mb: 3 }}>
      <CardHeader title="Navegação Rápida" />
      <CardContent>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            color="error"
            startIcon={<RiskIcon />}
            endIcon={<NavigateIcon />}
            onClick={() => handleNavigateTo('/risk-applications')}
          >
            Aplicar Riscos
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ExamIcon />}
            endIcon={<NavigateIcon />}
            onClick={() => handleNavigateTo('/exam-applications')}
          >
            Aplicar Exames
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<SourceIcon />}
            endIcon={<NavigateIcon />}
            onClick={() => handleNavigateTo('/source-applications')}
          >
            Aplicar Fontes Geradoras
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );

  const renderSummaryCards = () => {
    if (!consolidated) return null;

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <RiskIcon />
                <Box>
                  <Typography variant="h4">{consolidated.totals.risks}</Typography>
                  <Typography variant="body2">Riscos Aplicados</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ExamIcon />
                <Box>
                  <Typography variant="h4">{consolidated.totals.exams}</Typography>
                  <Typography variant="body2">Exames Aplicados</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <SourceIcon />
                <Box>
                  <Typography variant="h4">{consolidated.totals.sources}</Typography>
                  <Typography variant="body2">Fontes Geradoras</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ bgcolor: 'grey.700', color: 'grey.50' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CompanyIcon />
                <Box>
                  <Typography variant="h4">{consolidated.totals.total}</Typography>
                  <Typography variant="body2">Total de Aplicações</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderConsolidatedTable = (items: ConsolidatedItem[], type: ConsolidatedItemType) => {
    const paginatedItems = items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
      <DataTable
        columns={consolidatedColumns}
        rows={paginatedItems}
        getRowId={(item) => `${item.type}-${item.id}-${item.level}-${item.targetId}`}
        emptyState={{
          title: `Nenhum ${getConsolidatedItemTypeLabel(type).toLowerCase()} aplicado`,
          description:
            'Este quadro reúne o que foi aplicado à empresa, ao setor, à função e ao funcionário deste grupo.',
        }}
        pagination={
          items.length > rowsPerPage
            ? {
                page,
                rowsPerPage,
                totalCount: items.length,
                onPageChange: handleChangePage,
                onRowsPerPageChange: handleChangeRowsPerPage,
              }
            : undefined
        }
      />
    );
  };

  const renderConsolidatedView = () => {
    if (!localSelectedCompanyId) {
      return (
        <Alert severity="info">
          Selecione uma empresa para visualizar os dados consolidados.
        </Alert>
      );
    }

    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" onClose={clearError}>
          {error}
        </Alert>
      );
    }

    if (!consolidated) {
      return (
        <Alert severity="info">
          Nenhum dado consolidado disponível para esta empresa.
        </Alert>
      );
    }

    return (
      <Box>
        {renderSummaryCards()}

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab
            label={
              <Badge badgeContent={consolidated.totals.risks} color="error" max={999}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <RiskIcon fontSize="small" />
                  <span>Riscos</span>
                </Stack>
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={consolidated.totals.exams} color="primary" max={999}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ExamIcon fontSize="small" />
                  <span>Exames</span>
                </Stack>
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={consolidated.totals.sources} color="warning" max={999}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <SourceIcon fontSize="small" />
                  <span>Fontes Geradoras</span>
                </Stack>
              </Badge>
            }
          />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          {renderConsolidatedTable(consolidated.risks, 'risk')}
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          {renderConsolidatedTable(consolidated.exams, 'exam')}
        </TabPanel>
        <TabPanel value={activeTab} index={2}>
          {renderConsolidatedTable(consolidated.sources, 'source')}
        </TabPanel>
      </Box>
    );
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Segurança', path: '/seguranca' },
        { label: 'Grupo de Aplicação' },
      ]}
      title="Grupo de Aplicação"
      subtitle="Visualização consolidada de riscos, exames e fontes geradoras aplicados"
    >
      {/* Navigation Buttons */}
      {renderNavigationButtons()}

      {/* Company Selector */}
      {renderCompanySelector()}

      {/* Consolidated View */}
      <Card>
        <CardHeader title="Dados Consolidados da Empresa" />
        <CardContent>{renderConsolidatedView()}</CardContent>
      </Card>
    </PageShell>
  );
};

export default ApplicationGroupPage;
